import React, { useCallback, useContext, useEffect, useState } from 'react'
// import { useGetTimeSheets } from './hooks'
import {
  Avatar,
  Box,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Flag } from '@mui/icons-material'
import Excel from 'exceljs'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { useLocalStorage } from '../../../hooks/useLocalStorage'
import {
  convertUTCToTZ,
  formatDate,
  formatDateMMDDYYYY,
  formatTime,
  guessUserTZ,
} from '../../../lib/time'
import { Filter } from '../../../types/DataGrid'
import SnackBarContext from '../../../context/SnackBarContext'

export const ListTimesheets = (props: any) => {
  const {
    keyName,
    id,
    getQuery,
    updateQuery,
    showActionMenu,
    customToolBarConfig,
    userRoles,
    showCustomFilters,
    limitFieldViewForKiosk,
    recordStatusFilter,
    authUser,
    exportAsCSV,
    isCheckInOut,
    superintendents,    
  } = props
  const navigate = useNavigate()
  // Retrieve user's timezone setting
  const [settings] = useLocalStorage('settings', '')
  const userTZ = settings.timeZone ? settings.timeZone : guessUserTZ()
  const queryKey = [keyName, id]
  const toolBarConfig = customToolBarConfig
    ? customToolBarConfig
    : {
        add: false,
        export: userRoles && userRoles.includes('vtadmin') ? true : false,
        upload: false,
      }
  const [gridFilters, setGridFilters] = useState<Filter[]>([])
  const enableCustomFilters =
    showCustomFilters !== null ? showCustomFilters : false
  const hideFieldsForKiosk =
    limitFieldViewForKiosk !== null ? limitFieldViewForKiosk : false

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [checkedItems, setCheckedItems] = useState([])
  const { data, isFetching, error, refetch } = getQuery(
    queryKey[0],
    queryKey[1],
    {
      page,
      pageSize,
      recordStatusFilter,
    }
  )
  const {toggleSnackbar}=useContext(SnackBarContext)

  const columns: any[] = [
    { field: 'id', headerName: 'ID', width: 90, hide: true },
    {
      field: 'project',
      headerName: 'Company',
      width: 125,
      hide: hideFieldsForKiosk,
      valueGetter: (params: any) =>
        params.row?.project && params.row?.project.clientName,
    },
    {
      field: 'worker',
      headerName: 'Employee ID',
      width: 100,
      valueGetter: (params: any) => {
        if (params.row?.user) {
          return params.row?.user?.idNumber
        } else {
          return ''
        }
      },
    },
    {
      field: 'workerName',
      headerName: 'Worker Name',
      width: 150, // hide: !hideFieldsForKiosk,
      valueGetter: (params: any) => {
        if (params.row?.user) {
          return params.row?.user?.firstName + ' ' + params.row?.user?.lastName
        } else {
          return ''
        }
      },
    },
    {
      field: 'date',
      type: 'dateTime',
      headerName: 'Date Worked',
      valueGetter: (params: any) => {
        return params.row?.startTimeStamp
          ? formatDateMMDDYYYY(params.row?.startTimeStamp)
          : ''
      },
    },
    {
      field: 'startTimeStamp',
      type: 'dateTime',
      headerName: 'Start Time',
      valueGetter: (params: any) => {
        return params.row?.formattedTimeDurations?.startTime
          ? formatTime(params.row?.formattedTimeDurations?.startTime)
          : ''
      },
    },
    {
      field: 'endTimeStamp',
      type: 'dateTime',
      headerName: 'End Time',
      valueGetter: (params: any) => {
        if (!params.row?.formattedTimeDurations?.endTime) {
          return 'Open'
        }
        return formatTime(params.row?.formattedTimeDurations?.endTime)
      },
    },
    {
      field: 'regularTimeWorkedInMinutes',
      type: 'dateTime',
      headerName: 'Regular Time Worked',
      valueGetter: (params: any) => {
        if (!params.row?.formattedTimeDurations?.regularTimeWorked) {
          return 'Open'
        }
        return params.row?.formattedTimeDurations?.regularTimeWorked
      },
    },
    {
      field: 'overTimeWorkedInMinutes',
      type: 'dateTime',
      headerName: 'Overtime Worked',
      valueGetter: (params: any) => {
        if (!params.row?.formattedTimeDurations?.overTimeWorked) {
          return 'Open'
        }
        return params.row?.formattedTimeDurations?.overTimeWorked
      },
    },
    {
      field: 'doubleOverTimeWorkedInMinutes',
      type: 'dateTime',
      headerName: 'Dobletime Worked',
      valueGetter: (params: any) => {
        if (!params.row?.formattedTimeDurations?.doubleOverTimeWorked) {
          return 'Open'
        }
        return params.row?.formattedTimeDurations?.doubleOverTimeWorked
      },
    },
    {
      field: 'costCodes',
      headerName: 'Cost Codes',
      valueGetter: (params: any) => {
        if (params.row?.costCodes) {
          return params.row?.costCodes
            .map((code: any, index: number) => {
              return ` ${code?.costCode?.label} - ${code?.percentage}%`
            })
            .join(', ')
        }
      },
    },
    {
      field: 'q0',
      headerName: 'Injured',
      hide: hideFieldsForKiosk,
      valueGetter: (params: any) => {
        if (params.row?.clockOutAnswers && params.row?.clockOutAnswers[0]) {
          return params.row?.clockOutAnswers[0].answer ? 'Yes' : 'No'
        } else {
          return ''
        }
      },
    },
    {
      field: 'q1',
      headerName: 'Water',
      hide: hideFieldsForKiosk,
      valueGetter: (params: any) => {
        if (params.row?.clockOutAnswers && params.row?.clockOutAnswers[0]) {
          return params.row?.clockOutAnswers[1].answer ? 'Yes' : 'No'
        } else {
          return ''
        }
      },
    },
    {
      field: 'q2',
      headerName: 'Break',
      hide: hideFieldsForKiosk,
      valueGetter: (params: any) => {
        if (params.row?.clockOutAnswers && params.row?.clockOutAnswers[0]) {
          return params.row?.clockOutAnswers[2].answer ? 'Yes' : 'No'
        } else {
          return ''
        }
      },
    },
    {
      field: 'q4',
      headerName: 'Issue',
      hide: hideFieldsForKiosk,
      valueGetter: (params: any) => {
        if (params.row?.clockOutAnswers && params.row?.clockOutAnswers[0]) {
          return params.row?.clockOutAnswers[4].answer ? 'Yes' : 'No'
        } else {
          return ''
        }
      },
    },
    {
      field: 'q3',
      headerName: 'Lunch Break',
      hide: hideFieldsForKiosk,
      valueGetter: (params: any) => {
        if (params.row?.clockOutAnswers && params.row?.clockOutAnswers[0]) {
          return params.row?.clockOutAnswers[3].answer ? 'Yes' : 'No'
        } else {
          return ''
        }
      },
    },
    {
      field: 'finalizedDateUTC',
      type: 'dateTime',
      headerName: 'Date Finalized',
      width: 175,
      hide: true,
      valueGetter: ({ value }: any) => value && convertUTCToTZ(value, userTZ),
    },
  ]

  const formatCostCodesForExcel = (costCodes: any[]) => {
    if (!costCodes) return '';
    return costCodes.map(code => `${code.costCode.label} - ${code.percentage}%`).join('\n');
  };

  const exportToCSV = useCallback(async () => {
    if (exportAsCSV != null && data?.length > 0) {
      const workbook = new Excel.Workbook()
      const worksheet = workbook.addWorksheet('Sheet')
      worksheet.columns = columns.map(column => ({
        header: column.headerName,
        key: column.field,
        width: column.width || 20,
      }));
  
      data?.forEach((data: { [x: string]: any }) => {
        const rowData = columns.reduce((acc, column) => {
          // acc[column.field] = column.valueGetter 
          //   ? column.valueGetter({ row: data, value: data[column.field] }) 
          //   : data[column.field] ?? ''
          if (column.valueGetter) {
            if (column.field === 'costCodes') {
              acc[column.field] = formatCostCodesForExcel(data.costCodes);
            } else {
              acc[column.field] = column.valueGetter({ row: data, value: data[column.field] });
            }
          } else {
            acc[column.field] = data[column.field] ?? '';
          }
          return acc
        }, {})
        worksheet.addRow(rowData);
      })
  
      const buffer = await workbook.csv.writeBuffer()
      const blob = new Blob([buffer], { type: 'text/csv' })
      const fileName = `Timesheets ${moment().format('DD.MM.YY')}`
      const file = new File([blob], `${fileName}.csv`, { type: 'text/csv' })
      const fileURL = URL.createObjectURL(file)
  
      const downloadLink = document.createElement('a')
      downloadLink.href = fileURL
      downloadLink.download = `${fileName}.csv`
      document.body.appendChild(downloadLink)
      downloadLink.click()
    } 
    else if (exportAsCSV && data?.length == 0) {
      toggleSnackbar("No timesheets available to export", "error")
    }
  }, [exportAsCSV, data, toggleSnackbar])
  
  useEffect(() => {
    if (exportAsCSV) {
      exportToCSV()
    }
  }, [exportAsCSV, exportToCSV])

  useEffect(() => {
    refetch()
  }, [])

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{ marginTop: 2, boxShadow: 'none' }}>
        <Table
          sx={{
            minWidth: 650,
            boxShadow: 'none',
          }}
          aria-label='simple table'>
          <TableHead sx={{}}>
            <TableRow
              sx={{
                '& th': {
                  backgroundColor: '#24212C',
                  padding: '7px',
                  color: 'white',
                  fontWeight: 400,
                  whiteSpace: 'nowrap',
                },
                '& th:first-child': {
                  // alignItems: 'center',
                  // display: 'flex',
                  borderRadius: '10px 0px 0px 10px',
                },
                '& th:last-child': {
                  borderRadius: '0px 10px 10px 0px',
                },
              }}>
              <TableCell>
                <Box
                  style={{
                    flexDirection: 'row',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                  <Checkbox
                    checked={(data || [])?.every(
                      (row: any) => checkedItems[row?._id] == true
                    )}
                    onChange={e => {
                      setCheckedItems(
                        (data || []).reduce((a: any, b: any) => {
                          a[b._id] = e.target.checked
                          return a
                        }, {})
                      )
                    }}
                    sx={{ color: 'white', fill: 'white' }}
                  />
                  WORKER
                </Box>
              </TableCell>
              <TableCell>DATE</TableCell>
              {/* <TableCell>START TIME</TableCell>
              <TableCell>END TIME</TableCell> */}
              <TableCell>CHECK IN</TableCell>
              <TableCell>CHECK OUT</TableCell>
              <TableCell>FOREMAN</TableCell>
              <TableCell>COST CODE</TableCell>
              <TableCell>AUTOMATIC?</TableCell>
              <TableCell>TOTAL</TableCell>
              {showActionMenu && <TableCell>ACTIONS</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              '& td': {
                backgroundColor: 'white',
                color: '#5F5C5F',
                fontWeight: 400,
                whiteSpace: 'nowrap',
                padding: '7px',
                fontSize: 14,
              },
              '& td:first-child': {
                borderRadius: '10px 0px 0px 10px',
              },
              '& td:last-child': {
                borderRadius: '0px 10px 10px 0px',
              },
            }}>
            { data && data.length === 0 ?(<TableRow>
              <TableCell colSpan={12} style={{ textAlign: 'center' }}>
                No available data
              </TableCell>
            </TableRow>)
            :(data)?.map((row: any, index: any) => (
              <TableRow
                key={row?._id}
                sx={{
                  '& td, & th': {
                    border: 0,
                    background: index % 2 == 0 ? 'white' : '#F1EFF5',
                  },
                }}>
                <TableCell scope='row'>
                  <Box
                    style={{
                      flexDirection: 'row',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'left',
                    }}>
                    {checkedItems[row?._id] == true ? (
                      <Checkbox
                        checked={true}
                        onChange={e => {
                          setCheckedItems((prev: any) => {
                            return {
                              ...prev,
                              [row?._id]: e.target.checked,
                            }
                          })
                        }}
                        sx={{ color: '#D9D9D9', fill: 'white' }}
                      />
                    ) : (
                      <Checkbox
                        checked={false}
                        onChange={e => {
                          setCheckedItems((prev: any) => {
                            return {
                              ...prev,
                              [row?._id]: e.target.checked,
                            }
                          })
                        }}
                        sx={{ color: '#D9D9D9', fill: 'white' }}
                      />
                    )}
                    <Avatar
                      src={row?.user?.idPhoto}
                      alt={row?.name}
                      sx={{ marginRight: 1, width: 35, height: 35 }}
                    />
                    <Box
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '15ch',
                      }}>
                      {row?.user?.firstName} {row?.user?.lastName}
                    </Box>
                  </Box>
                </TableCell>
                {/* <TableCell>{row?.project?.projectName}</TableCell> */}
                {/* <TableCell>{formatTime(row?.startTimeStamp)}</TableCell> */}
                {/* <TableCell>{formatTime(row?.workEndDateTimeUTC)}</TableCell> */}
                <TableCell>
                  {formatDate(row?.formattedTimeDurations?.startTime)}
                </TableCell>
                <TableCell>
                  {formatTime(row?.formattedTimeDurations?.startTime)}
                </TableCell>
                <TableCell>
                  {row?.formattedTimeDurations?.endTime
                    ? formatTime(row?.formattedTimeDurations?.endTime)
                    : 'Open'}
                </TableCell>
                <TableCell>
                  <Box
                    style={{
                      flexDirection: 'row',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'left',
                    }}>
                    <Avatar
                      src={
                        keyName === 'projectTimesheets' ||
                        keyName === 'kioskTimesheets'
                        ? superintendents[0]?.idPhoto?superintendents[0]?.idPhoto:superintendents[0]?.picture                            
                        : row?.project?.superintendents[0]?.idPhoto?row?.project?.superintendents[0]?.idPhoto:
                        row?.project?.superintendents[0]?.picture
                      }
                      alt={'foreman picture'}
                      sx={{ marginRight: 1, width: 35, height: 35 }}
                    />
                    {keyName === 'projectTimesheets' ||
                    keyName === 'kioskTimesheets'
                      ? superintendents[0]?.firstName ?? '----'
                      : row?.project?.superintendents[0]?.firstName ?? '----'}
                    {keyName === 'projectTimesheets' ||
                    keyName === 'kioskTimesheets'
                      ? superintendents[0]?.lastName ?? '----'
                      : row?.project?.superintendents[0]?.lastName ?? '----'}
                  </Box>
                </TableCell>
                <TableCell>
                  {row?.costCodes
                    ?.map(
                      (costCode: any, index: number) =>
                        costCode?.costCode?.label
                    )
                    .join(', ') || '---'}
                </TableCell>
                <TableCell>
                  {row?.automaticClockout ? <Flag color='success' /> : null}
                </TableCell>
                <TableCell>
                  <Box
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      display: 'flex',
                      justifyContent: 'left',
                      gap: 0,
                    }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        alignItems: 'center',
                        justifyContent: 'center',
                        display: 'flex',
                        gap: 0.6,
                      }}>
                      <svg
                        width='16'
                        height='17'
                        viewBox='0 0 16 17'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'>
                        <path
                          d='M1.4375 8.21582C1.4375 9.9563 2.1289 11.6255 3.35961 12.8562C4.59032 14.0869 6.25952 14.7783 8 14.7783C9.74048 14.7783 11.4097 14.0869 12.6404 12.8562C13.8711 11.6255 14.5625 9.9563 14.5625 8.21582C14.5625 6.47534 13.8711 4.80614 12.6404 3.57543C11.4097 2.34472 9.74048 1.65332 8 1.65332C6.25952 1.65332 4.59032 2.34472 3.35961 3.57543C2.1289 4.80614 1.4375 6.47534 1.4375 8.21582Z'
                          stroke='#F0845D'
                          strokeLinecap='round'
                          stroke-linejoin='round'
                        />
                        <path
                          d='M8 8.21582V5.87207'
                          stroke='#F0845D'
                          strokeLinecap='round'
                          stroke-linejoin='round'
                        />
                        <path
                          d='M8 8.21582L10.9294 11.1458'
                          stroke='#F0845D'
                          strokeLinecap='round'
                          stroke-linejoin='round'
                        />
                      </svg>
                      <span style={{ color: '#F0845D' }}>Regular:</span>{' '}
                      {row?.formattedTimeDurations?.regularTimeWorked
                        ? row?.formattedTimeDurations?.regularTimeWorked
                        : 'Open'}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        alignItems: 'center',
                        justifyContent: 'center',
                        display: 'flex',
                        gap: 0.6,
                      }}>
                      {row?.formattedTimeDurations?.overTimeWorked != null &&
                      row?.formattedTimeDurations?.overTimeWorked !== '0:00' ? (
                        <>
                          <svg
                            width='16'
                            height='17'
                            viewBox='0 0 16 17'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'>
                            <path
                              d='M1.4375 8.21582C1.4375 9.9563 2.1289 11.6255 3.35961 12.8562C4.59032 14.0869 6.25952 14.7783 8 14.7783C9.74048 14.7783 11.4097 14.0869 12.6404 12.8562C13.8711 11.6255 14.5625 9.9563 14.5625 8.21582C14.5625 6.47534 13.8711 4.80614 12.6404 3.57543C11.4097 2.34472 9.74048 1.65332 8 1.65332C6.25952 1.65332 4.59032 2.34472 3.35961 3.57543C2.1289 4.80614 1.4375 6.47534 1.4375 8.21582Z'
                              stroke='#7AB3B3'
                              strokeLinecap='round'
                              stroke-linejoin='round'
                            />
                            <path
                              d='M8 8.21582V5.87207'
                              stroke='#7AB3B3'
                              strokeLinecap='round'
                              stroke-linejoin='round'
                            />
                            <path
                              d='M8 8.21582L10.9294 11.1458'
                              stroke='#7AB3B3'
                              strokeLinecap='round'
                              stroke-linejoin='round'
                            />
                          </svg>
                          <span style={{ color: '#7AB3B3' }}>Overtime:</span>{' '}
                          {row?.formattedTimeDurations?.overTimeWorked}{' '}
                        </>
                      ) : null}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        alignItems: 'center',
                        justifyContent: 'center',
                        display: 'flex',
                        gap: 0.6,
                      }}>
                      {row?.formattedTimeDurations?.doubleOverTimeWorked !=
                        null &&
                      row?.formattedTimeDurations?.doubleOverTimeWorked !==
                        '0:00' ? (
                        <>
                          <svg
                            width='16'
                            height='17'
                            viewBox='0 0 16 17'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'>
                            <path
                              d='M1.4375 8.21582C1.4375 9.9563 2.1289 11.6255 3.35961 12.8562C4.59032 14.0869 6.25952 14.7783 8 14.7783C9.74048 14.7783 11.4097 14.0869 12.6404 12.8562C13.8711 11.6255 14.5625 9.9563 14.5625 8.21582C14.5625 6.47534 13.8711 4.80614 12.6404 3.57543C11.4097 2.34472 9.74048 1.65332 8 1.65332C6.25952 1.65332 4.59032 2.34472 3.35961 3.57543C2.1289 4.80614 1.4375 6.47534 1.4375 8.21582Z'
                              stroke='#7C7A80'
                              strokeLinecap='round'
                              stroke-linejoin='round'
                            />
                            <path
                              d='M8 8.21582V5.87207'
                              stroke='#7C7A80'
                              strokeLinecap='round'
                              stroke-linejoin='round'
                            />
                            <path
                              d='M8 8.21582L10.9294 11.1458'
                              stroke='#7C7A80'
                              strokeLinecap='round'
                              stroke-linejoin='round'
                            />
                          </svg>
                          <span style={{ color: '#7C7A80' }}>Doubletime:</span>{' '}
                          {row?.formattedTimeDurations?.doubleOverTimeWorked}
                        </>
                      ) : null}
                    </Typography>
                  </Box>
                </TableCell>
                {showActionMenu && (
                  <TableCell>
                    <Box
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 5,
                      }}>
                      <TimeSheetMenu data={row} />
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* <Box
        alignItems={"flex-end"}
        display={"flex"}
        justifyContent={"end"}
        mt={1}
      >
        <Pagination
          count={pageCount || 0}
          onChange={(event, page) => setPage(page)}
          color="primary"
        />
      </Box> */}
      {/* <DataList
        columns={columns}
        queryKey={queryKey}
        useGetItems={getQuery}
        useEditMutate={updateQuery}
        toolBarItems={toolBarConfig}
        gridFilters={gridFilters}
        setGridFilters={setGridFilters}
        addAction={() => navigate("timesheet")}
        customToolBar={ListCustomToolBar}
        isCellEditable={(params: GridCellParams) =>
          !params.row?.finalizedDateUTC
        }
        viewLink="timecard/edit"
        includeRowEdit={false}
        includeRowDelete={false}
        showActionMenu={showActionMenu}
        recordStatusFilter={recordStatusFilter}
      /> */}
    </>
  )
}

const TimeSheetMenu = ({ data }: any) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const navigate = useNavigate()
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }
  return (
    <>
      <Box
        sx={{
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          display: 'flex',
          paddingLeft: 3,
          cursor: 'pointer',
        }}>
        <IconButton
          sx={{ width: 35, height: 35 }}
          onClick={(event: any) => {
            setAnchorEl(event.currentTarget)
          }}>
          <svg
            width='16'
            height='6'
            viewBox='0 0 16 6'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'>
            <path
              d='M0 3.00065C0 3.57529 0.228273 4.12639 0.634602 4.53272C1.04093 4.93904 1.59203 5.16732 2.16667 5.16732C2.7413 5.16732 3.2924 4.93904 3.69873 4.53272C4.10506 4.12639 4.33333 3.57529 4.33333 3.00065C4.33333 2.42602 4.10506 1.87492 3.69873 1.46859C3.2924 1.06226 2.7413 0.833984 2.16667 0.833984C1.59203 0.833984 1.04093 1.06226 0.634602 1.46859C0.228273 1.87492 0 2.42602 0 3.00065Z'
              fill='#A5A2A5'
            />
            <path
              d='M5.8335 3.00065C5.8335 3.57529 6.06177 4.12639 6.4681 4.53272C6.87443 4.93904 7.42553 5.16732 8.00016 5.16732C8.5748 5.16732 9.1259 4.93904 9.53223 4.53272C9.93856 4.12639 10.1668 3.57529 10.1668 3.00065C10.1668 2.42602 9.93856 1.87492 9.53223 1.46859C9.1259 1.06226 8.5748 0.833984 8.00016 0.833984C7.42553 0.833984 6.87443 1.06226 6.4681 1.46859C6.06177 1.87492 5.8335 2.42602 5.8335 3.00065Z'
              fill='#A5A2A5'
            />
            <path
              d='M11.6665 3.00065C11.6665 3.57529 11.8948 4.12639 12.3011 4.53272C12.7074 4.93904 13.2585 5.16732 13.8332 5.16732C14.4078 5.16732 14.9589 4.93904 15.3652 4.53272C15.7716 4.12639 15.9998 3.57529 15.9998 3.00065C15.9998 2.42602 15.7716 1.87492 15.3652 1.46859C14.9589 1.06226 14.4078 0.833984 13.8332 0.833984C13.2585 0.833984 12.7074 1.06226 12.3011 1.46859C11.8948 1.87492 11.6665 2.42602 11.6665 3.00065Z'
              fill='#A5A2A5'
            />
          </svg>
        </IconButton>
      </Box>

      <Menu
        id='basic-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}>
        <MenuItem
          onClick={() => {
            handleClose()
            navigate('timecard/edit/:' + data?.id)
          }}>
          View
        </MenuItem>
      </Menu>
    </>
  )
}
